import { useRef, useState } from 'react';
import { defaultGreeting, greetingStatus, type AssistantVoice, type GreetingMode, type Language } from '@acs/shared';
import { Banner, BrandLoader, Button, Field, Icon, IconButton, Input, SegmentedControl, Select, Textarea } from '@acs/ui';
import { voiceSamplePath } from './voiceSample.js';
import { useI18n } from '../../i18n/index.js';
import type { TranslationKey } from '../../i18n/index.js';

/**
 * The assistant's settings as form fields, controlled — shared by Settings ›
 * Assistant and the setup wizard's assistant step, so an owner meets the same
 * controls in both places and the wizard cannot quietly ask for less than the
 * settings page will later show them.
 *
 * Two groups, because they are two questions: how the assistant sounds
 * ({@link AssistantFields}) and what it knows about the business
 * ({@link FactsFields}). Each host owns its own state and its own Save.
 */

/** Minutes offered in the call-length picker. */
const CALL_LENGTH_OPTIONS = [5, 10, 15, 20, 30] as const;

/** Voices, grouped the way owners choose them. Names are OpenAI's own, kept
 *  verbatim so support can match what an owner says to what is stored. */
const VOICE_OPTIONS: { labelKey: TranslationKey; voices: AssistantVoice[] }[] = [
  { labelKey: 'settings.assistant.voiceFemale', voices: ['marin', 'coral', 'sage'] },
  { labelKey: 'settings.assistant.voiceMale', voices: ['cedar', 'ash', 'verse'] },
];

/** Longest notes the assistant is given — mirrors `firestore.rules`. */
export const FACTS_TEXT_MAX = 1500;

/** How the receptionist behaves on a call: the language it opens in, the voice
 *  it speaks with, the words it opens with, and how long a single call may run.
 *
 *  The daily call cap is deliberately NOT here. It was a number an owner could
 *  set with no say in what happens once it is hit — the assistant simply stops
 *  answering, and a caller who rings a business that has gone quiet is a lost
 *  customer, not a saved minute. The server-side default stays as an abuse
 *  guard; minutes are what an owner actually budgets, and those have their own
 *  meter under Subscription. */
export function AssistantFields({
  language,
  onLanguage,
  voice,
  onVoice,
  greeting,
  onGreeting,
  greetingMode,
  onGreetingMode,
  businessName,
  maxCallSeconds,
  onMaxCallSeconds,
  greetingRequired = false,
}: {
  language: Language;
  onLanguage: (v: Language) => void;
  voice: AssistantVoice;
  onVoice: (v: AssistantVoice) => void;
  greeting: string;
  onGreeting: (v: string) => void;
  /** Doc 71: our sentence from the current name, or the owner's own words. */
  greetingMode: GreetingMode;
  onGreetingMode: (v: GreetingMode) => void;
  /** The name the standard greeting says — previewed exactly as callers hear it. */
  businessName: string;
  maxCallSeconds: number;
  onMaxCallSeconds: (v: number) => void;
  /** The wizard refuses to continue without one; Settings saves what it has. */
  greetingRequired?: boolean;
}) {
  const { t } = useI18n();
  return (
    <>
      {/* The language the ASSISTANT speaks to callers — a property of the
          business, not of whoever is reading this page. The portal's own
          language is in the account menu (doc 20 D1). */}
      <Field label={t('settings.assistant.language')} hint={t('settings.assistant.languageHint')}>
        {/* Endonyms, never translated — same rule as the account menu's
            language list: you pick a language by the name it calls itself. */}
        <Select value={language} onChange={(e) => onLanguage(e.target.value as Language)}>
          <option value="fr">Français</option>
          <option value="de">Deutsch</option>
          <option value="en">English</option>
          <option value="pl">Polski</option>
        </Select>
      </Field>

      {/* The sample button sits ON the picker rather than under it: choosing a
          voice and hearing it are one action, and a button on its own line
          reads as belonging to whatever field follows it. */}
      <Field label={t('settings.assistant.voice')} hint={t('settings.assistant.voiceHint')}>
        <div className="voice-pick">
          <Select value={voice} onChange={(e) => onVoice(e.target.value as AssistantVoice)}>
            {VOICE_OPTIONS.map(({ labelKey, voices }) => (
              <optgroup key={labelKey} label={t(labelKey)}>
                {voices.map((v) => (
                  <option key={v} value={v}>
                    {v.charAt(0).toUpperCase() + v.slice(1)}
                  </option>
                ))}
              </optgroup>
            ))}
          </Select>
          {/* Keyed on the sample: a language or voice change remounts the
              player instead of mutating one mid-clip. */}
          <VoicePreview key={`${language}-${voice}`} voice={voice} language={language} />
        </div>
      </Field>

      {/* Doc 71: the standard greeting is not a copied sentence but a mode,
          rendered from the CURRENT name — a rename can never leave the old
          one behind. Own words are kept exactly as written; when they are our
          sentence for another name (left over from an earlier rename), the
          owner is told and offered the standard one in one click. */}
      <Field
        label={t('settings.assistant.greeting')}
        required={greetingRequired && greetingMode === 'custom'}
        hint={t('settings.assistant.greetingHint')}
      >
        <div className="greeting-mode">
          <SegmentedControl<GreetingMode>
            aria-label={t('settings.assistant.greeting')}
            value={greetingMode}
            onChange={onGreetingMode}
            options={[
              { value: 'default', label: t('settings.assistant.greetingStandard') },
              { value: 'custom', label: t('settings.assistant.greetingCustom') },
            ]}
          />
          {greetingMode === 'default' ? (
            <>
              <p className="greeting-preview" data-testid="greeting-preview">
                {defaultGreeting(businessName.trim(), language)}
              </p>
              <p className="field-hint">{t('settings.assistant.greetingStandardHint')}</p>
            </>
          ) : (
            <>
              <GreetingStaleBanner
                businessName={businessName}
                language={language}
                greeting={greeting}
                onUseStandard={() => onGreetingMode('default')}
              />
              <Textarea value={greeting} onChange={(e) => onGreeting(e.target.value)} rows={3} maxLength={300} />
            </>
          )}
        </div>
      </Field>

      {/* Enforced server-side but with no UI, so the only way to change it was
          editing Firestore by hand. */}
      <Field label={t('settings.assistant.maxCall')} hint={t('settings.assistant.maxCallHint')}>
        <Select value={String(maxCallSeconds)} onChange={(e) => onMaxCallSeconds(Number(e.target.value))}>
          {CALL_LENGTH_OPTIONS.map((min) => (
            <option key={min} value={min * 60}>
              {t('settings.assistant.minutes', { count: min })}
            </option>
          ))}
        </Select>
      </Field>
    </>
  );
}

/** Own words that are OUR sentence for another name: callers hear an old name (doc 71). */
function GreetingStaleBanner({
  businessName,
  language,
  greeting,
  onUseStandard,
}: {
  businessName: string;
  language: Language;
  greeting: string;
  onUseStandard: () => void;
}) {
  const { t } = useI18n();
  const status = greetingStatus({ name: businessName, language, greeting, greetingMode: 'custom' });
  if (status.kind !== 'stale') return null;
  return (
    <Banner
      tone="warning"
      actions={
        <Button size="sm" variant="secondary" onClick={onUseStandard}>
          {t('settings.assistant.greetingUseStandard')}
        </Button>
      }
    >
      {t('settings.assistant.greetingStale', { old: status.staleName, name: businessName.trim() })}
    </Banner>
  );
}

/**
 * What the assistant knows about the business beyond its services and hours
 * (doc 23 P1) — the address, then everything else.
 *
 * The address gets a field; the rest stays one textarea. Structured fields for
 * parking, access and payment would be the obvious next step and are
 * deliberately not taken — they would decide in advance what a business is
 * allowed to be asked about, and the dead-end counter on the calls page is what
 * will tell us which fields to add once the pattern is real rather than
 * assumed. The address is the one exception because it is the question callers
 * ask most and everyone already knows how to fill it in.
 */
export function FactsFields({
  address,
  onAddress,
  text,
  onText,
}: {
  address: string;
  onAddress: (v: string) => void;
  text: string;
  onText: (v: string) => void;
}) {
  const { t } = useI18n();
  return (
    <>
      <Field label={t('settings.facts.address')} hint={t('settings.facts.addressHelp')}>
        <Input
          value={address}
          onChange={(e) => onAddress(e.target.value)}
          maxLength={200}
          placeholder={t('settings.facts.addressPlaceholder')}
        />
      </Field>

      <Field label={t('settings.facts.label')} hint={t('settings.facts.help')}>
        <Textarea
          value={text}
          onChange={(e) => onText(e.target.value)}
          rows={9}
          maxLength={FACTS_TEXT_MAX}
          placeholder={t('settings.facts.placeholder')}
        />
      </Field>
      <p className="field-hint">{t('settings.facts.counter', { used: text.length, max: FACTS_TEXT_MAX })}</p>
    </>
  );
}

/**
 * Hear a voice before committing to it.
 *
 * Twenty-four prerecorded samples — six voices x the four languages the
 * assistant speaks — served straight from `public/voices`. Deliberately NOT
 * synthesised per press: the answer is one of twenty-four fixed clips, so a
 * round trip would spend money and a spinner to say something we already know,
 * and the whole value of this control is that it is instant.
 *
 * The sample is not the owner's greeting. It is a fixed line in the assistant's
 * language, because what this control is about is the voice — the only part of
 * the choice that can be recorded in advance. Hearing their own words on their
 * own line is what Test call is for, and the field hint still says so.
 *
 * It follows the PENDING language and voice, not the saved ones: the point is to
 * listen before pressing Save. The caller remounts it per sample (see the `key`
 * above), so there is no half-played element to reason about and the previous
 * voice can never keep playing under the new label.
 */
function VoicePreview({ voice, language }: { voice: AssistantVoice; language: Language }) {
  const { t } = useI18n();
  const audioRef = useRef<HTMLAudioElement>(null);
  const [state, setState] = useState<'idle' | 'loading' | 'playing' | 'failed'>('idle');

  // `loading` counts as playing for the button: a second press has to be able
  // to stop a sample that has been asked for but not yet started.
  const playing = state === 'playing' || state === 'loading';
  const label = t(playing ? 'settings.assistant.voiceStop' : 'settings.assistant.voicePlay', {
    voice: voice.charAt(0).toUpperCase() + voice.slice(1),
  });

  const toggle = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) {
      audio.pause();
      setState('idle');
      return;
    }
    setState('loading');
    audio.currentTime = 0;
    // A missing or unplayable clip rejects here *and* fires `error`; both land
    // on the same state, so the button stops offering what it cannot do rather
    // than looking broken on every press.
    void audio.play().catch(() => setState('failed'));
  };

  return (
    <>
      <IconButton
        aria-label={label}
        title={label}
        variant="ghost"
        disabled={state === 'failed'}
        onClick={toggle}
        icon={
          // `aria-hidden`: the button already carries the label, and a
          // `role="status"` inside it would announce the same thing twice.
          state === 'loading' ? (
            <BrandLoader size={18} aria-hidden />
          ) : (
            <Icon name={playing ? 'pause' : 'play'} size={18} />
          )
        }
      />
      {/* Said out loud rather than swallowed: a disabled button with no reason
          beside it reads as the page being broken. */}
      {state === 'failed' && (
        <span className="field-hint">{t('settings.assistant.voiceSampleMissing')}</span>
      )}
      {/* `preload="none"` — twenty-four clips must not be fetched because a
          settings page was opened; the first press is what loads one. */}
      <audio
        ref={audioRef}
        src={voiceSamplePath(language, voice)}
        preload="none"
        onPlaying={() => setState('playing')}
        onEnded={() => setState('idle')}
        onError={() => setState('failed')}
      />
    </>
  );
}
